import { CardControl } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/CardControl';
import { QuickExpand } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/QuickExpand';
import { QuickSave } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/QuickSave';
import { TextValuePreview } from 'components/detail/edit/TextValuePreview';
import { GradientLine } from 'components/detail/minute/GradientLine';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSectionHeader } from 'elements/minute/sidebar/sidebarSection/SidebarSection/SidebarSectionHeader';
import { motion } from 'framer-motion';
import { conditionalScaleAnimation } from 'libs/styles/animations';
import { EHovers } from 'libs/styles/styling';
import { useContext } from 'react';
import { CtxDetail } from 'session/ctxDetail';
import { Localization } from 'static/localization/localization';
import styled, { css } from 'styled-components/macro';

interface Props {
    position: number;
    className?: string;
}

export const EditLinkedStoriesCard = (props: Props): JSX.Element => {
    const ctxDetail = useContext(CtxDetail);
    const card = ctxDetail.updateDetailInput.cards[props.position];
    const isUpdatingIndex = ctxDetail.updatingIndex === props.position;

    return (
        <motion.div {...conditionalScaleAnimation(true, 1.02, 1.02)}>
            <S_MinuteCard
                className={props.className}
                $isUpdatingIndex={isUpdatingIndex}
                onClick={() => {
                    if (!isUpdatingIndex) {
                        ctxDetail.setUpdatingIndex(props.position);
                    }
                }}
            >
                <S_SidebarSectionHeader
                    $isUpdatingIndex={isUpdatingIndex}
                    icon={{
                        iconName: 'list'
                    }}
                    title={Localization.LINKED_DETAILS}
                    highlighted={!!isUpdatingIndex}
                    topBorder
                >
                    {isUpdatingIndex && <QuickSave />}
                    {!isUpdatingIndex && <QuickExpand />}
                </S_SidebarSectionHeader>
                {!isUpdatingIndex && (
                    <S_Content>
                        <S_TextValuePreview
                            value={
                                (card?.linkedStories?.storyIds?.length ?? 0) +
                                ' ' +
                                Localization.DETAILS
                            }
                        />
                    </S_Content>
                )}
                {isUpdatingIndex && (
                    <S_ExpandedContent>
                        <SidebarContent>{Localization.NO_EDIT_IN_MAP_EDITOR}</SidebarContent>
                        <CardControl position={props.position} />
                        <S_GradientLine />
                    </S_ExpandedContent>
                )}
            </S_MinuteCard>
        </motion.div>
    );
};

const S_MinuteCard = styled.div<{ $isUpdatingIndex: boolean }>`
    display: flex;
    flex-direction: column;
    border-right: 1px solid #d3d3d3;
    border-left: 1px solid #d3d3d3;
    ${(props) => {
        if (props.$isUpdatingIndex) {
            return css`
                border-right: 1px solid #8f3a19;
                border-left: 1px solid #b19052;
            `;
        }
    }}
    ${(props) => {
        if (!props.$isUpdatingIndex) {
            return css`
                &:hover {
                    ${S_SidebarSectionHeader} {
                        box-shadow: ${EHovers.DARK1};
                    }
                    ${S_Content} {
                        box-shadow: ${EHovers.LIGHT4};
                        cursor: pointer;
                    }
                }
            `;
        }
    }}
`;

const S_SidebarSectionHeader = styled(SidebarSectionHeader)<{ $isUpdatingIndex: boolean }>`
    cursor: ${(props) => (props.$isUpdatingIndex ? 'unset' : 'pointer')};
`;

const S_Content = styled.div`
    display: flex;
    flex-direction: column;
`;

const S_ExpandedContent = styled.div`
    padding-top: 20px;
`;

const S_TextValuePreview = styled(TextValuePreview)``;

const S_GradientLine = styled(GradientLine)`
    margin: 15px 0 0 0;
`;
